// ============================================================================
// LA MARGE QUI NE SE MESURE PAS — l'encart qui le dit, et la liste des articles en cause.
//
// UTILITÉ : tant qu'une ligne vendue n'a pas de coût connu, la marge d'une période ne se calcule pas. Le Résumé (la
// répartition des 100 €, la tuile Marge, la matrice) et le tableau de bord le disent ici, en clair — combien d'articles,
// combien de lignes — et ouvrent la liste qui permet d'agir : la fiche d'un article sans prix d'achat, le code d'un
// article inconnu du catalogue.
//
// ⚠️ UN SEUL ENCART ET UNE SEULE LISTE pour tous les écrans : chacun écrivait sa phrase, et l'une d'elles disait « inconnu
//    du catalogue » d'un article qui y était. La CAUSE vient du serveur (`sales/sale-lines.ts`, `costGap`).
// ⚠️ AUCUNE MARGE PARTIELLE : une marge calculée sur les seuls articles au coût connu se lirait comme la vraie.
// ============================================================================
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Copyable, EmptyState, Icon, Modal, showToast } from '@orgazone/ui-web';
import { formatMoney } from '@orgazone/money-format';
import { api, type MissingCostArticleView, type MissingCostsParams, type SalesMarginGap } from '../api';
import { t } from '../i18n';
import { shownValue } from './stats-format';

/**
 * Le titre de l'encart — la cause, dite par ce qu'il faut faire.
 * ⚠️ PURE ET EXPORTÉE : c'est la phrase que la garde mesure, sans monter d'écran.
 */
export function missingCostTitle(gap: SalesMarginGap): string {
  const { purchasePrice, unknownArticle } = gap.byCause;
  if (unknownArticle === 0) return t('Il manque le prix d’achat de {n} article', { n: purchasePrice });
  if (purchasePrice === 0) return t('{n} code vendu est inconnu du catalogue', { n: unknownArticle });
  return t('{n} article sans coût connu', { n: gap.codeCount });
}

/**
 * L'ENCART : ce qui manque, pourquoi la marge attend, et le bouton qui ouvre la liste.
 * `compact` : une ligne, pour un bloc qui en énumère plusieurs (le tableau de bord).
 */
export function MissingCostNotice({ gap, query, compact = false }: { gap: SalesMarginGap; query: MissingCostsParams; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const { purchasePrice, unknownArticle } = gap.byCause;
  const button = (
    <Button size="sm" icon="search" onClick={() => setOpen(true)} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
      {t('Voir les articles')}
    </Button>
  );
  return (
    <>
      {compact ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>{missingCostTitle(gap)} — {t('{n} ligne vendue sans coût connu', { n: gap.lines })}.</span>
          {button}
        </span>
      ) : (
        <div role="note" style={{
          display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 'var(--r-sm)',
          background: 'var(--ink-50)', border: '1px solid var(--ink-200)',
        }}>
          <span style={{ width: 30, height: 30, borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="info" size={15} />
          </span>
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <strong style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)' }}>{missingCostTitle(gap)}</strong>
            <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.5 }}>
              {t('{n} ligne vendue sur la période — sans leur coût, la marge ne se calcule pas : une marge partielle serait fausse.', { n: gap.lines })}
            </span>
            {purchasePrice > 0 && unknownArticle > 0 && (
              <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
                {t('{n} sans prix d’achat', { n: purchasePrice })} · {t('{n} inconnu du catalogue', { n: unknownArticle })}
              </span>
            )}
          </span>
          {button}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={missingCostTitle(gap)}
        subtitle={t('Le plus gros chiffre d’affaires en tête : complétez d’abord ce qui pèse.')} icon="info" width="xxl">
        {open && <MissingCostList query={query} />}
      </Modal>
    </>
  );
}

/** Le libellé et la teinte d'une cause. */
const CAUSES = {
  purchase_price: { tone: 'warn' as const, label: () => t('Prix d’achat manquant') },
  unknown_article: { tone: 'danger' as const, label: () => t('Inconnu du catalogue') },
};

/**
 * LA LISTE : chaque article, sa cause, ses lignes et son chiffre HT, et le geste qui règle la cause.
 * ⚠️ « OUVRIR LA FICHE » cherche l'article au catalogue par son code et l'ouvre dans l'écran Articles, là où son prix
 *    d'achat se saisit. Un code inconnu n'a pas de fiche à ouvrir : on donne le code, copiable, pour la créer.
 */
function MissingCostList({ query }: { query: MissingCostsParams }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<MissingCostArticleView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // ⚠️ RELUE QUAND LA REQUÊTE CHANGE DE CONTENU, pas d'identité : un objet recréé à chaque rendu relirait sans fin.
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    let alive = true;
    api.salesMissingCosts(JSON.parse(queryKey) as MissingCostsParams)
      .then((v) => { if (alive) setRows(v.rows); })
      .catch((e: unknown) => { if (alive) setError(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [queryKey]);

  const openSheet = async (code: string) => {
    try {
      const page = await api.articles({ page: 1, size: 8, sort: 'designation', dir: 'asc', q: code });
      const model = page.rows.find((r) => r.gencode === code) ?? (page.rows.length === 1 ? page.rows[0] : undefined);
      if (!model) { showToast(t('L’article {code} n’a pas été retrouvé au catalogue.', { code }), 'err'); return; }
      navigate('/articles', { state: { ficheDepuisPalette: model } });
    } catch (e) {
      showToast(t('L’article n’a pas pu être ouvert : {error}.', { error: e instanceof Error ? e.message : String(e) }), 'err');
    }
  };

  if (error) return <div role="alert" style={{ color: 'var(--danger-ink)', fontSize: 'var(--t-body)' }}>{t('La liste n’a pas pu être lue : {error}.', { error })}</div>;
  if (!rows) return <EmptyState icon="search" title={t('Lecture des articles…')} note={t('Les ventes de la période sont relues.')} />;
  if (rows.length === 0) return <EmptyState icon="check" title={t('Plus aucun article sans coût')} note={t('La marge de cette période se calcule désormais : rechargez la page.')} />;
  const grid = 'minmax(0,1fr) 170px 56px 104px 190px';
  return (
    <div role="table" aria-label={t('Articles sans coût connu')} style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
      <div role="row" style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, padding: '8px 12px', background: 'var(--ink-50)', fontSize: '11px', fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
        <span role="columnheader">{t('Article')}</span>
        <span role="columnheader">{t('Cause')}</span>
        <span role="columnheader" style={{ textAlign: 'right' }}>{t('Lignes')}</span>
        <span role="columnheader" style={{ textAlign: 'right' }}>{t('CA HT')}</span>
        <span role="columnheader" />
      </div>
      {rows.map((r) => (
        <div key={r.code} role="row" style={{ display: 'grid', gridTemplateColumns: grid, gap: 12, alignItems: 'center', padding: '9px 12px', borderTop: '1px solid var(--ink-100)', fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>
          <span role="cell" style={{ minWidth: 0 }}>
            <span style={{ display: 'block', color: 'var(--ink-900)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label || shownValue(r.code)}</span>
            <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--ink-500)' }}>{shownValue(r.code)}</span>
          </span>
          <span role="cell" style={{ minWidth: 0 }}><Badge tone={CAUSES[r.cause].tone}>{CAUSES[r.cause].label()}</Badge></span>
          <span role="cell" style={{ textAlign: 'right' }}>{r.lines}</span>
          <span role="cell" style={{ textAlign: 'right' }}>{formatMoney(r.netHtCents)}</span>
          <span role="cell" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {r.cause === 'purchase_price' ? (
              <Button size="sm" variant="solid" onClick={() => void openSheet(r.code)}
                aria-label={t('Ouvrir la fiche de {article}', { article: r.label || r.code })} style={{ whiteSpace: 'nowrap' }}>
                {t('Ouvrir la fiche')}
              </Button>
            ) : (
              <Copyable value={r.code} label={t('Copier le code {code}', { code: r.code })}>
                <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-600)' }}>{t('Créez la fiche à ce code')}</span>
              </Copyable>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}
